import {css, html, nothing, PropertyValues, TemplateResult} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {SpDialog, SpDialogTemplate} from './sp-dialog';
import {repeat} from 'lit-html/directives/repeat.js';
import {styleMap} from 'lit-html/directives/style-map.js';
import {DirectiveResult} from 'lit/directive';
import {perfectScrollbar} from '../../styles/3rd-party/perfect-scrollbar.styles';
import {SpButtonTextPosition} from '../sp-button/sp-button';
import {SpScrollbarController} from '../../controllers/sp-scrollbar.controller';
import {when} from "lit-html/directives/when.js";

@customElement('sp-default-dialog')
export class SpDefaultDialog extends SpDialog {

    @property({type: String})
    headerText: TemplateResult | DirectiveResult | string;
    @property({type: Array})
    buttons: SpDefaultDialogButtonConfig[] = [];
    @property({type: Array})
    checkboxes: SpDefaultDialogCheckboxConfig[] = [];
    @property({type: Boolean})
    suppressBodyScrollbar = false;
    @property({type: String})
    alignBody: 'start' | 'center' | 'end';
    @property({type: String})
    justifyBody: 'start' | 'center' | 'end';

    originalDialogInstance: SpDefaultDialog;
    scrollbarController = new SpScrollbarController(this);

    static styles = [
        perfectScrollbar,
        ...SpDialog.styles,
        //language=css
        css`
            .container {
                display: flex;
                flex-direction: column;
                background-color: rgb(var(--sp-greylight));
                min-height: 14rem;
                max-height: calc(100vh - calc(2*var(--sp-default-padding)));
                height: 100%;
            }

            .header {
                background-color: rgb(var(--sp-greydark));
                padding: calc(var(--sp-default-padding) / 2);
                text-transform: uppercase;
                font-size: 1rem;
                color: white;
            }
            
            .body {
                position: relative;
                flex-grow: 1;
                margin: calc(var(--sp-default-padding) / 2);
            }

            .footer {
                display: flex;
                flex-direction: column;
                gap: calc(var(--sp-default-padding) / 2);
                padding: 0 calc(var(--sp-default-padding) / 2) calc(var(--sp-default-padding) / 2) calc(var(--sp-default-padding) / 2);
            }
            
            sp-checkbox:last-of-type {
                margin-bottom: 2px;
            }
            
            .button-row {
                display: flex;
                flex-direction: row;
            }
            
            .footer sp-button:not(:last-child) {
                margin-right: calc(var(--sp-default-padding) / 2)
            }

            sp-button {
                width: 100%;
            }
        `
    ];

    updateButtons(updates: { index: number, button: SpDefaultDialogButtonConfig | Partial<SpDefaultDialogButtonConfig> | null, reducerMode?: 'merge' | 'replace' }[]) {
        if (!this.buttons) this.buttons = [];
        updates.forEach((val) => {
            if (!val.button) this.buttons.splice(val.index, 1);
            else {
                if (!this.buttons[val.index]) this.buttons.push(val.button as SpDefaultDialogButtonConfig)
                else {
                    if (!val.reducerMode || val.reducerMode === 'merge') this.buttons[val.index] = {...this.buttons[val.index], ...val.button}
                    else this.buttons[val.index] = val.button as SpDefaultDialogButtonConfig
                }
            }
        });
        this.requestUpdate();
    }

    firstUpdated(_changedProperties: PropertyValues) {
        super.firstUpdated(_changedProperties);
        if (!this.suppressBodyScrollbar && !this.renderTarget) {
            this.scrollbarController.add(this.renderRoot.querySelector('.body'));
        }
        // Only needed for legacy reasons in the spotteron-app, which uses fastclick.js and cannot set classes for nested components
        this.shadowRoot.host.classList.add('needsclick');
    }

    getTemplate() {
        return html`
            <sl-dialog no-header
                       @sl-request-close=${ event => { this.handleCloseViaOverlay(event) } }
                       @sl-show=${() => this.emitEvent('show')}
                       @sl-after-show=${() => this.emitEvent('after-show')}
                       @sl-hide=${() => this.emitEvent('hide')}
                       @sl-after-hide=${() => this.emitEvent('after-hide')}>
                <div class="container" style=${styleMap({
                    ...this.fullHeight && {height: 'calc(100vh - calc(2*var(--sp-default-padding)))'}
                })}>
                    ${this.headerText ? html`
                        <div part="header" class="header">${this.headerText}</div>` : nothing}
                    <div part="body" 
                         class="body"
                         style=${ styleMap({
                             ...(this.alignBody || this.justifyBody) && { 'display': 'flex' }, 
                             ...this.alignBody && { 'align-items': this.alignBody },
                             ...this.justifyBody && { 'justify-content': this.justifyBody }
                         })}>
                        ${this.destroy ? nothing : this.template}
                    </div>
                    ${ when(!this.destroy && ((this.buttons && this.buttons.length > 0) || (this.checkboxes && this.checkboxes.length > 0)), () => html`
                                <div part="footer" class="footer">
                                    ${ when(this.buttons?.length > 0, () => html`
                                        <div class="button-row">
                                            ${ repeat(this.buttons, button => {
                                                return html`
                                            <sp-button
                                                    style=${ styleMap({
                                                    ...button.backgroundColor && { '--sp-button-background-color': button.backgroundColor }
                                                }) }
                                                    .icon=${button.icon}
                                                    .iconSide=${button.iconSide}
                                                    .text=${button.text}
                                                    .textPosition=${button.textPosition}
                                                    ?disabled=${button.disabled}
                                                    @click=${event => {
                                                    const shadowRoot = this.renderTarget ? this.renderTargetInstance.shadowRoot : this.shadowRoot;
                                                    const elem = shadowRoot.querySelector('.body').children;
                                                    button.clickCallback(elem, this, this.checkboxes);
                                                }}></sp-button>`
                                            }) }
                                        </div>
                                    `)}
                                    ${repeat(this.checkboxes, checkbox => {
                                        return html`
                                            <sp-checkbox
                                                .checked=${!!checkbox.checked}
                                                .label=${checkbox.label}
                                                .disabled=${checkbox.disabled}
                                                variant="minimal"
                                                @change=${ evt => checkbox.checked = evt.detail.checked }>
                                            </sp-checkbox>`
                                    })}
                                </div>`)}
                </div>
            </sl-dialog>`;
    }

    protected getInstance() {
        return new SpDefaultDialog();
    }
}

export interface SpDefaultDialogButtonConfig {
    clickCallback: (content: HTMLCollection, dialog: SpDefaultDialog, checkboxes: SpDefaultDialogCheckboxConfig[]) => any;
    disabled?: boolean;
    iconSide?: 'left' | 'right';
    icon?: string;
    text?: SpDialogTemplate;
    backgroundColor?: string;
    textPosition?: SpButtonTextPosition;
}

export interface SpDefaultDialogCheckboxConfig {
    identifier: string;
    checked?: boolean;
    label?: string;
    disabled?: boolean;
}
